import { createForOfIteratorHelper as _createForOfIteratorHelper, toConsumableArray as _toConsumableArray } from '../../_virtual/_rollupPluginBabelHelpers.esm.js';

/*
 *  Ported code from react-aria-components as of V.1.2.1 use the original commit in the repo
 *  to diff any added code since the original commit.
 */

// Keeps a ref count of all hidden elements. Added to when hiding an element, and
// subtracted from when showing it again. When it reaches zero, aria-hidden is removed.
var refCountMap = new WeakMap();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var observerStack = [];

/**
 * Hides all elements in the DOM outside the given targets from screen readers using aria-hidden,
 * and returns a function to revert these changes. In addition, changes to the DOM are watched
 * and new elements outside the targets are automatically hidden.
 * @param targets - The elements that should remain visible.
 * @param root - Nothing will be hidden above this element.
 * @returns - A function to restore all hidden elements.
 */
function ariaHideOutside(targets) {
  var root = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document.body;
  var visibleNodes = new Set(targets);
  var hiddenNodes = new Set();
  var walk = function walk(root) {
    // Keep live announcer and top layer elements (e.g. toasts) visible.
    var _iterator = _createForOfIteratorHelper(root.querySelectorAll('[data-live-announcer], [data-react-aria-top-layer]')),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var element = _step.value;
        visibleNodes.add(element);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    var acceptNode = function acceptNode(node) {
      // Skip this node and its children if it is one of the target nodes, or a live announcer.
      // Also skip children of already hidden nodes, as aria-hidden is recursive. An exception is
      // made for elements with role="row" since VoiceOver on iOS has issues hiding elements with role="row".
      // For that case we want to hide the cells inside as well (https://bugs.webkit.org/show_bug.cgi?id=222623).
      if (visibleNodes.has(node) || node.parentElement && hiddenNodes.has(node.parentElement) && node.parentElement.getAttribute('role') !== 'row') {
        return NodeFilter.FILTER_REJECT;
      }

      // Skip this node but continue to children if one of the targets is inside the node.
      var _iterator2 = _createForOfIteratorHelper(visibleNodes),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var target = _step2.value;
          if (node.contains(target)) {
            return NodeFilter.FILTER_SKIP;
          }
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
      return NodeFilter.FILTER_ACCEPT;
    };
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
      acceptNode: acceptNode
    });

    // TreeWalker does not include the root.
    var acceptRoot = acceptNode(root);
    if (acceptRoot === NodeFilter.FILTER_ACCEPT) {
      hide(root);
    }
    if (acceptRoot !== NodeFilter.FILTER_REJECT) {
      var node = walker.nextNode();
      while (node != null) {
        hide(node);
        node = walker.nextNode();
      }
    }
  };
  var hide = function hide(node) {
    var _refCountMap$get;
    var refCount = (_refCountMap$get = refCountMap.get(node)) !== null && _refCountMap$get !== void 0 ? _refCountMap$get : 0;

    // If already aria-hidden, and the ref count is zero, then this element
    // was already hidden and there's nothing for us to do.
    if (node.getAttribute('aria-hidden') === 'true' && refCount === 0) {
      return;
    }
    if (refCount === 0) {
      node.setAttribute('aria-hidden', 'true');
    }
    hiddenNodes.add(node);
    refCountMap.set(node, refCount + 1);
  };

  // If there is already a MutationObserver listening from a previous call,
  // disconnect it so the new on takes over.
  if (observerStack.length) {
    observerStack[observerStack.length - 1].disconnect();
  }
  walk(root);
  var observer = new MutationObserver(function (changes) {
    var _iterator3 = _createForOfIteratorHelper(changes),
      _step3;
    try {
      var _loop = function _loop() {
        var change = _step3.value;
        if (change.type !== 'childList' || change.addedNodes.length === 0) {
          return 1; // continue
        }

        // If the parent element of the added nodes is not within one of the targets,
        // and not already inside a hidden node, hide all of the new children.
        if (![].concat(_toConsumableArray(visibleNodes), _toConsumableArray(hiddenNodes)).some(function (node) {
          return node.contains(change.target);
        })) {
          var _iterator4 = _createForOfIteratorHelper(change.removedNodes),
            _step4;
          try {
            for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
              var node = _step4.value;
              if (node instanceof Element) {
                visibleNodes["delete"](node);
                hiddenNodes["delete"](node);
              }
            }
          } catch (err) {
            _iterator4.e(err);
          } finally {
            _iterator4.f();
          }
          var _iterator5 = _createForOfIteratorHelper(change.addedNodes),
            _step5;
          try {
            for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
              var _node = _step5.value;
              if ((_node instanceof HTMLElement || _node instanceof SVGElement) && (_node.dataset.liveAnnouncer === 'true' || _node.dataset.reactAriaTopLayer === 'true')) {
                visibleNodes.add(_node);
              } else if (_node instanceof Element) {
                walk(_node);
              }
            }
          } catch (err) {
            _iterator5.e(err);
          } finally {
            _iterator5.f();
          }
        }
      };
      for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
        if (_loop()) continue;
      }
    } catch (err) {
      _iterator3.e(err);
    } finally {
      _iterator3.f();
    }
  });
  observer.observe(root, {
    childList: true,
    subtree: true
  });
  var observerWrapper = {
    observe: function observe() {
      observer.observe(root, {
        childList: true,
        subtree: true
      });
    },
    disconnect: function disconnect() {
      observer.disconnect();
    }
  };
  observerStack.push(observerWrapper);
  return function () {
    observer.disconnect();
    var _iterator6 = _createForOfIteratorHelper(hiddenNodes),
      _step6;
    try {
      for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
        var _refCountMap$get2;
        var node = _step6.value;
        var count = (_refCountMap$get2 = refCountMap.get(node)) !== null && _refCountMap$get2 !== void 0 ? _refCountMap$get2 : 0;
        if (count === 1) {
          node.removeAttribute('aria-hidden');
          refCountMap["delete"](node);
        } else if (count > 0) {
          refCountMap.set(node, count - 1);
        }
      }

      // Remove this observer from the stack, and start the previous one.
    } catch (err) {
      _iterator6.e(err);
    } finally {
      _iterator6.f();
    }
    if (observerWrapper === observerStack[observerStack.length - 1]) {
      observerStack.pop();
      if (observerStack.length) {
        observerStack[observerStack.length - 1].observe();
      }
    } else {
      observerStack.splice(observerStack.indexOf(observerWrapper), 1);
    }
  };
}

export { ariaHideOutside as default };
//# sourceMappingURL=aria-hide-outside.esm.js.map
